import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Link, useParams } from 'react-router-dom';
import PATH_PAGE from '@/router/path';
import { Building2, User, ChevronRight, Server, Layers, Settings } from 'lucide-react';
import { useDemoMode } from '@/context/DemoModeContext';
import { useGetWorkspaces } from '@/service/workspace/hook';
import { RouteParams } from '@/router/type';
import { useDemoWorkspaceStore, DemoWorkspace } from '@/store/demo-workspace';
import { useDemoOrganizationStore, DemoOrganization } from '@/store/demo-organization';

interface WorkspaceOverviewProps {
  showAll?: boolean; // If true, show all workspaces. If false, show only current workspace from URL
}

const WorkspaceOverview = ({ showAll = false }: WorkspaceOverviewProps) => {
  const { isDemoMode } = useDemoMode();
  const { orgId, workspaceId } = useParams<RouteParams>();
  const demoWorkspaces = useDemoWorkspaceStore((state) => state.workspaces);
  const demoOrganizations = useDemoOrganizationStore((state) => state.organizations);
  const { data: realWorkspaces } = useGetWorkspaces({
    staleTime: 1000 * 60 * 5,
    enabled: !isDemoMode,
  });

  const getOrgPath = (orgId: string) => {
    return isDemoMode ? `/demo/dashboard/${orgId}` : `/dashboard/${orgId}`;
  };

  const getOrgSettingsPath = (orgId: string) => {
    const basePath = PATH_PAGE.dashboard.orgSetting.general(orgId);
    return isDemoMode ? `/demo${basePath}` : basePath;
  };

  const getWorkspacePath = (orgId: string, workspaceId: string) => {
    return isDemoMode ? `/demo/dashboard/${orgId}/${workspaceId}` : `/dashboard/${orgId}/${workspaceId}`;
  };

  const getInstancesPath = (orgId: string, workspaceId: string) => {
    const basePath = PATH_PAGE.dashboard.gpuInstance.instances(orgId, workspaceId);
    return isDemoMode ? `/demo${basePath}` : basePath;
  };

  // Demo mode: Show organizations with nested workspaces
  if (isDemoMode && showAll) {
    // Filter organizations by orgId from URL if present
    const displayOrganizations = orgId
      ? demoOrganizations.filter((org) => org.id === orgId)
      : demoOrganizations;

    return (
      <div className="space-y-8">
        {displayOrganizations.map((org: DemoOrganization) => {
          const orgWorkspaces = demoWorkspaces.filter((w) => w.organizationId === org.id);
          const orgTotalQuota = Object.values(org.quota).reduce((sum, q) => sum + q.total, 0);
          const orgUsedQuota = Object.values(org.quota).reduce((sum, q) => sum + q.used, 0);

          return (
            <Card key={org.id} className="border-2">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg bg-orange-500/10 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <CardTitle className="flex items-center gap-2 text-xl">
                        {org.name}
                        <Badge variant="outline" className="text-xs font-normal capitalize">
                          {org.role}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        <Layers className="h-3 w-3" />
                        {orgWorkspaces.length} workspace{orgWorkspaces.length !== 1 ? 's' : ''}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {orgTotalQuota > 0 && (
                      <div className="text-right">
                        <div className="text-2xl font-bold">{orgUsedQuota}/{orgTotalQuota}</div>
                        <p className="text-xs text-muted-foreground">Organization GPU Quota</p>
                      </div>
                    )}
                    <Link to={getOrgSettingsPath(org.id)}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to={getOrgPath(org.id)}>
                      <Button variant="outline" size="sm">
                        Open <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Organization GPU Quota */}
                {orgTotalQuota > 0 && (
                  <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                    {Object.entries(org.quota).map(([key, q]) => {
                      if (q.total === 0) return null;
                      const label = key === 'h200' ? 'H200' : key === 'a100' ? 'A100' : key === 'l4' ? 'L4' : key.toUpperCase();
                      const pct = q.total > 0 ? (q.used / q.total) * 100 : 0;
                      return (
                        <div key={key} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{label}</span>
                            <span className="font-medium">{q.used}/{q.total}</span>
                          </div>
                          <Progress value={pct} className="h-1.5" />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Workspaces under this organization */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-muted-foreground">Workspaces</h4>
                  <div className="grid gap-3">
                    {orgWorkspaces.map((workspace: DemoWorkspace) => {
                      const wsTotalQuota = Object.values(workspace.quota).reduce((sum, q) => sum + q.total, 0);
                      const wsUsedQuota = Object.values(workspace.quota).reduce((sum, q) => sum + q.used, 0);
                      const runningCount = workspace.instances.reduce((sum, i) => sum + (i.count || 1), 0);

                      return (
                        <Card key={workspace.id} className="bg-background">
                          <CardHeader className="py-3 px-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {workspace.type === 'personal' ? (
                                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <User className="h-4 w-4 text-primary" />
                                  </div>
                                ) : (
                                  <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                    <Building2 className="h-4 w-4 text-blue-500" />
                                  </div>
                                )}
                                <div>
                                  <CardTitle className="text-base flex items-center gap-2">
                                    {workspace.name}
                                    {workspace.role && (
                                      <Badge variant="secondary" className="text-xs font-normal">
                                        {workspace.role}
                                      </Badge>
                                    )}
                                  </CardTitle>
                                  <CardDescription className="text-xs">
                                    {workspace.type === 'personal' ? 'Personal' : 'Team workspace'}
                                    {runningCount > 0 && (
                                      <span className="ml-2 text-green-600">
                                        • {runningCount} running
                                      </span>
                                    )}
                                  </CardDescription>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {wsTotalQuota > 0 && (
                                  <div className="text-right">
                                    <div className="text-lg font-semibold">{wsUsedQuota}/{wsTotalQuota}</div>
                                    <p className="text-xs text-muted-foreground">GPUs</p>
                                  </div>
                                )}
                                <Link to={getWorkspacePath(org.id, workspace.id)}>
                                  <Button variant="ghost" size="sm">
                                    Open <ChevronRight className="h-4 w-4 ml-1" />
                                  </Button>
                                </Link>
                              </div>
                            </div>
                          </CardHeader>
                          {workspace.instances.length > 0 && (
                            <CardContent className="py-2 px-4 border-t">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Server className="h-3 w-3" />
                                  {workspace.instances.slice(0, 2).map((inst, idx) => (
                                    <span key={idx} className="flex items-center gap-1">
                                      <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                      {inst.name}
                                      {idx < Math.min(workspace.instances.length - 1, 1) && ','}
                                    </span>
                                  ))}
                                  {workspace.instances.length > 2 && (
                                    <span>+{workspace.instances.length - 2} more</span>
                                  )}
                                </div>
                                <Link to={getInstancesPath(org.id, workspace.id)}>
                                  <Button variant="ghost" size="sm" className="h-6 text-xs">
                                    View All
                                  </Button>
                                </Link>
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  // Single workspace view (when inside a workspace)
  const allWorkspaces = isDemoMode
    ? demoWorkspaces.map((w: DemoWorkspace) => ({
        id: w.id,
        name: w.name,
        organizationId: w.organizationId,
        type: w.type,
        role: w.role,
        quota: w.quota,
        instances: w.instances,
      }))
    : (realWorkspaces?.map((w) => ({
        id: w.workspace_id || '',
        name: w.workspace_name || '',
        organizationId: 'default',
        type: 'workspace' as const,
        role: undefined,
        quota: {
          gpu: { total: 0, used: 0 },
        },
        instances: [],
      })) || []);

  const workspaces = showAll
    ? allWorkspaces
    : allWorkspaces.filter((w) => w.id === workspaceId);

  return (
    <div className="space-y-8">
      {workspaces.map((workspace) => {
        const totalQuota = Object.values(workspace.quota).reduce((sum, q) => sum + q.total, 0);
        const usedQuota = Object.values(workspace.quota).reduce((sum, q) => sum + q.used, 0);
        const runningCount = workspace.instances.reduce((sum, i) => sum + (i.count || 1), 0);
        const wsOrgId = workspace.organizationId || orgId || 'default';

        return (
          <Card key={workspace.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {workspace.type === 'personal' ? (
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                  ) : (
                    <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-500" />
                    </div>
                  )}
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {workspace.name}
                      {workspace.role && (
                        <Badge variant="outline" className="text-xs font-normal">
                          {workspace.role}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {workspace.type === 'personal' ? 'Personal workspace' : 'Team workspace'}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {totalQuota > 0 && (
                    <div className="text-right">
                      <div className="text-2xl font-bold">{usedQuota}/{totalQuota}</div>
                      <p className="text-xs text-muted-foreground">GPUs used</p>
                    </div>
                  )}
                  {showAll && (
                    <Link to={getWorkspacePath(wsOrgId, workspace.id)}>
                      <Button variant="outline" size="sm">
                        Open <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </CardHeader>
            {(Object.keys(workspace.quota).length > 0 && totalQuota > 0) && (
              <CardContent className="space-y-4">
                {/* GPU Quota */}
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(workspace.quota).map(([key, q]) => {
                    if (q.total === 0) return null;
                    const label = key === 'h200' ? 'H200' : key === 'a100' ? 'A100' : key === 'l4' ? 'L4' : key.toUpperCase();
                    const pct = q.total > 0 ? (q.used / q.total) * 100 : 0;
                    return (
                      <div key={key} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{label}</span>
                          <span className="font-medium">{q.used}/{q.total}</span>
                        </div>
                        <Progress value={pct} className="h-1.5" />
                      </div>
                    );
                  })}
                </div>

                {/* Running Instances */}
                {workspace.instances.length > 0 && (
                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Server className="h-4 w-4" />
                        Running ({runningCount})
                      </div>
                      <Link to={getInstancesPath(wsOrgId, workspace.id)}>
                        <Button variant="ghost" size="sm" className="h-7 text-xs">
                          View All <ChevronRight className="h-3 w-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                    <div className="grid gap-2">
                      {workspace.instances.slice(0, 3).map((inst, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm p-2 rounded bg-muted/50">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            <span>{inst.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground text-xs">{inst.type}</span>
                            {inst.count && inst.count > 1 && (
                              <Badge variant="secondary" className="text-xs">{inst.count}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                      {workspace.instances.length > 3 && (
                        <p className="text-xs text-muted-foreground text-center">
                          +{workspace.instances.length - 3} more
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default WorkspaceOverview;
